import React from "react";
import { Tooltip } from "antd";
import Icons from "components/icons";
import { Localize } from "components/service";
import {
    formatStaffAttendanceDate,
    formatStaffAttendanceDuration,
    formatStaffAttendanceTime,
} from "../helpers/staff-attendance-api-helper";
import StaffAttendanceStatusField from "./staff-attendance-status-field";
import {
    getStaffAttendanceSessionsCount,
    getStaffAttendanceWarningTranslationKey,
    hasStaffAttendanceWarningCode,
    STAFF_ATTENDANCE_WARNING_CODES,
} from "../helpers/staff-attendance-session-helper";
import StaffAttendanceSessionDetailsField from "./staff-attendance-session-details-field";

const StaffAttendanceWorkerRowField = ({
    attendanceRow,
    groupBy,
    expanded,
    onToggle,
    onOpenLocation,
    manualCheckout,
    scrollRef,
}) => {
    const workerName = `${ attendanceRow.worker?.name || "" } ${ attendanceRow.worker?.surname || "" }`.trim();
    const sessionsCount = getStaffAttendanceSessionsCount(attendanceRow);
    const isCompleted = attendanceRow.status === "completed";
    const warningTranslationKey = getStaffAttendanceWarningTranslationKey(attendanceRow.warnings);
    const workedMoreThanNineHours = hasStaffAttendanceWarningCode(
        attendanceRow.warnings,
        STAFF_ATTENDANCE_WARNING_CODES.WORKED_MORE_THAN_NINE_HOURS
    );
    const detailsID = `staff-attendance-worker-details-${ attendanceRow.id }`;

    const stopRowToggle = (event) => event.stopPropagation();

    return (
        <div
            ref={ scrollRef }
            className={ `staff-attendance-worker${ expanded ? " staff-attendance-worker--expanded" : "" }` }
            role="rowgroup">
            <div
                className="row-grid no-gap staff-attendance-worker-row"
                role="row"
                onClick={ onToggle }>
                <div role="cell" />
                <div className="staff-attendance-worker-row__worker" role="cell">
                    { workerName || <>&mdash;</> }
                </div>
                <div className="staff-attendance-worker-row__context" role="cell">
                    { groupBy === "subcontractor" ?
                        formatStaffAttendanceDate(attendanceRow.work_date) :
                        attendanceRow.subcontractor?.title || <>&mdash;</>
                    }
                </div>
                <div role="cell">{ formatStaffAttendanceTime(attendanceRow.first_check_in) }</div>
                <div role="cell">
                    { isCompleted ?
                        formatStaffAttendanceTime(attendanceRow.last_check_out) :
                        <StaffAttendanceStatusField
                            status={ attendanceRow.status }
                            warningCodes={ attendanceRow.warnings } />
                    }
                </div>
                <div
                    className={ `d-flex staff-attendance-worker-row__total${ workedMoreThanNineHours ? " staff-attendance-worker-row__total--warning" : "" }` }
                    role="cell">
                    { isCompleted ?
                        <>
                            { formatStaffAttendanceDuration(attendanceRow.total_worked_seconds) }
                            { sessionsCount > 1 &&
                                <span className="staff-attendance-worker-row__sessions">
                                    (<Localize count={ sessionsCount }>STAFF_MANAGEMENT.Text_SessionsCount</Localize>)
                                </span>
                            }
                            { warningTranslationKey &&
                                <Tooltip
                                    overlayClassName="staff-attendance-warning-tooltip"
                                    placement="bottomLeft"
                                    trigger={ [ "hover", "focus", "click" ] }
                                    title={ <Localize>{ warningTranslationKey }</Localize> }>
                                        <button
                                            type="button"
                                            className="d-flex align-items-center justify-content-center staff-attendance-warning-icon"
                                            onClick={ stopRowToggle }>
                                            <Icons.StaffInfo aria-hidden="true" />
                                            <span className="visually-hidden">
                                                <Localize>{ warningTranslationKey }</Localize>
                                            </span>
                                        </button>
                                    </Tooltip>
                            }
                        </> :
                        <>&mdash;</>
                    }
                </div>
                <div role="cell">
                    <button
                        type="button"
                        className="d-flex align-items-center justify-content-center cursor-pointer staff-attendance-worker-row__details-button"
                        aria-expanded={ expanded }
                        aria-controls={ detailsID }
                        onClick={ (event) => {
                            event.stopPropagation();
                            onToggle();
                        } }>
                        <span aria-hidden="true"><Icons.Arrow /></span>
                        <span className="visually-hidden">
                            <Localize>STAFF_MANAGEMENT.Action_ToggleSessionDetails</Localize>
                        </span>
                    </button>
                </div>
            </div>

            { expanded &&
                <div
                    id={ detailsID }
                    className="staff-attendance-worker__details-row"
                    role="row">
                    <div role="cell" aria-colspan="7">
                        <StaffAttendanceSessionDetailsField
                            attendanceRow={ attendanceRow }
                            manualCheckout={ manualCheckout }
                            onOpenLocation={ onOpenLocation } />
                    </div>
                </div>
            }
        </div>
    );
};

export default StaffAttendanceWorkerRowField;
